import { HttpFailedToFetchException } from "../model/httpException";
import { getAuthHeaders } from "../utils/authHeaders";
import { logException } from "../utils/logUtils";
import { fetchWithRetry } from "../utils/retry.util";

const IMAGE_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".bmp",
  ".webp",
  ".svg",
  ".ico",
];

export class ProjectFilesApi {
  private static readonly BASE_URL = import.meta.env.VITE_API_URL;

  static async loadProjectFilesList(
    projectId: string
  ): Promise<{ [key: string]: any }[]> {
    const headers = await getAuthHeaders({});

    const response = await fetchWithRetry(
      `${this.BASE_URL}/projectfileslist?projectId=${projectId}`,
      {
        method: "GET",
        headers,
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logException("Project Files API error (/projectfileslist):", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new HttpFailedToFetchException();
    }

    const responseJson = await response.json();
    return responseJson;
  }

  static async loadProjectFile(
    projectId: string,
    revisionId: string,
    filePath: string
  ): Promise<string> {
    // Check if the file is an image
    const isImage = IMAGE_EXTENSIONS.some((ext) =>
      filePath.toLowerCase().endsWith(ext)
    );

    const headers = await getAuthHeaders({});

    const response = await fetchWithRetry(
      `${this.BASE_URL}/file?projectId=${projectId}&revisionId=${revisionId}&filePath=${filePath}`,
      {
        method: "GET",
        headers,
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logException("Project Files API error (/file):", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new HttpFailedToFetchException();
    }

    if (isImage) {
      // For images, get the response as blob and convert to base64
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result as string;
          // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
          const base64Content = base64data.split(",")[1];
          resolve(base64Content);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } else {
      // For non-image files, return as text
      return response.text();
    }
  }
}
